/** @format */

import {
	Box,
	Button,
	CircularProgress,
	Container,
	Grid,
	Pagination,
	Stack,
	Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
	CircleLimitedDetails,
	CircleSearch,
	LoadingSpinner,
} from "../allComponents/AllComponents";
import CircleDiscoveryCard from "./CircleDiscoveryCard";
import { checkCircleCountryEligibility } from "../../utility/circleCountryEligibility";
import { useDispatch, useSelector } from "react-redux";
import {
	selectAllCircles,
	selectCircleError,
	selectIsLoading,
	selectJoinRequestErrorMessage,
	selectJoinRequestSuccessMessage,
} from "../../redux/selectors/circleSelector";
import { AppDispatch } from "../../redux/store/store";
import { fetchAllCircles } from "../../redux/actions/circleAction";
import { selectCurrentUser } from "../../redux/selectors/userSelector";
import { useNavigate } from "react-router-dom";
// import useCheckAuthenticationOnPageLoad from "../check-auth-onPage-load/CheckAuthOnPageLoad";
import { Alert, Snackbar } from "@mui/material";
import { clearJoinMessage } from "../../redux/reducers/circleReducer";

import JoinRequestForm from "../modal/join-request-modal/JoinRequestForm";
import UserProfileHeader from "../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../drawerNavBar/DrawerNavbar";
import Main from "../../utility/common/Main";
import {
	isEndOfMonthWindow,
	getJoinWindowMessage,
	isCycleInProgress,
} from "../../utility/utils/mid-cycle-join-utils";
import {
	circleMembersInclude,
	userUnionsInclude,
} from "../../utility/utils/circle-membership";

interface Circle {
	_id?: string;
	circleName?: string;
	circleImage?: string;
}

const AllCirclesView = () => {
	const [openNav, setOpenNav] = useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();
	const userData = useSelector(selectCurrentUser);
	const circlesData = useSelector(selectAllCircles);
	const circles = Array.isArray(circlesData) ? circlesData : [];
	const isLoading = useSelector(selectIsLoading);
	const error = useSelector(selectCircleError);
	const joinRequestSuccessMessage = useSelector(
		selectJoinRequestSuccessMessage,
	);
	const joinRequestErrorMessage = useSelector(selectJoinRequestErrorMessage);
	const [selectedCircle, setSelectedCircle] = useState<Circle | null>(null);
	const [searchInput, setSearchInput] = useState<string>("");
	const [showCircleLimitedDetails, setShowCircleLimitedDetails] =
		useState(false);
	const [selectedCircleId, setSelectedCircleId] = useState<string | null>(null);
	const [openAlert, setOpenAlert] = useState(false);
	const [alertType, setAlertType] = useState<"success" | "error">("success");
	const [alertMessage, setAlertMessage] = useState("");
	const [refresh, setRefresh] = useState(false);
	const [joinRequestModalOpen, setJoinRequestModalOpen] =
		useState<boolean>(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage] = useState(12);

	useEffect(() => {
		const fetchData = async () => {
			await dispatch(fetchAllCircles()).unwrap();
		};

		fetchData();
	}, [dispatch, refresh]);

	useEffect(() => {
		if (joinRequestSuccessMessage) {
			setAlertType("success");
			setAlertMessage(joinRequestSuccessMessage);
			setOpenAlert(true);

			dispatch(clearJoinMessage());
		}
	}, [joinRequestSuccessMessage, dispatch]);

	useEffect(() => {
		// If there's an error message in redux, show error alert
		if (joinRequestErrorMessage?.trim() !== "") {
			setAlertType("error");
			setAlertMessage(joinRequestErrorMessage);
			setOpenAlert(true);

			dispatch(clearJoinMessage());
		}
	}, [joinRequestErrorMessage, dispatch]);

	const filteredCircle = circles
		.filter((circle) => !circle.isBanned) // Exclude banned circles
		.filter((circle) =>
			circle.circleName?.toLowerCase().includes(searchInput.toLowerCase()),
		);

	const totalPages = Math.ceil(filteredCircle.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;

	const paginatedCircles =
		selectedCircle ?
			[selectedCircle]
			: filteredCircle.slice(startIndex, endIndex);

	const handleSearchInputChange = (inputValue: string) => {
		setSearchInput(inputValue);
		setSelectedCircle(null);
	};

	const handleSearchSelect = (circle: Circle | null) => {
		setSelectedCircle(circle);
		setSearchInput(""); // Clear search input when selecting.
	};

	const handleReset = () => {
		setSearchInput("");
		setSelectedCircle(null);
		setCurrentPage(1);
	};

	const handleRedirect = () => {
		navigate(`/user/${userData?._id}`);
	};

	const handleOpenJoinModal = (circleId: string) => {
		setSelectedCircleId(circleId);
		setJoinRequestModalOpen(true);
	};

	const handleCloseJoinModal = () => {
		setJoinRequestModalOpen(false);
		setSelectedCircleId(null);
	};

	const handleViewCircle = (circleId: string) => {
		navigate(`/circle/${circleId}`);
	};

	const handleMoreIconClick = (circleId: string) => {
		setSelectedCircleId(circleId);
		setShowCircleLimitedDetails(true);
	};

	const handleCloseCircleDetails = () => {
		setShowCircleLimitedDetails(false);
		setSelectedCircleId(null);
	};

	const handleCloseAlert = () => {
		setOpenAlert(false);
		setAlertMessage("");
	};

	const handlePageChange = (
		event: React.ChangeEvent<unknown>,
		value: number,
	) => {
		setCurrentPage(value);
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	};

	const selectedCircleData = circles.find((c) => c._id === selectedCircleId);

	const handleActionComplete = () => {
		dispatch(fetchAllCircles());
		setRefresh((prev) => !prev);
	};

	return (
		<>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />

			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				{/* <Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				mb={5}
				mt={5}>
				<Typography variant="h4">All Circles</Typography>

				<Button
					// variant="contained"
					startIcon={<Iconify icon="lets-icons:back" />}
					onClick={handleRedirect}
					sx={{
						color: buttonColors.primary,
						"&:hover": {
							backgroundColor: "transparent",
						},
					}}>
					Back to dashboard
				</Button>
			</Stack> */}

				{isLoading ?
					<LoadingSpinner
						loading={isLoading}
						size={45}
						sx={{ position: "absolute" }}
					/>
					: <DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />
				}

				<Main>
					<Stack
						mb={5}
						direction="row"
						alignItems="center"
						justifyContent="space-between">
						<CircleSearch
							circles={filteredCircle}
							selectedCircle={selectedCircle}
							onSearchInputChange={handleSearchInputChange}
							onSearchSelect={handleSearchSelect}
							onReset={handleReset}
						/>
					</Stack>

					{!selectedCircle && filteredCircle.length > 0 && (
						<Box
							sx={{
								mb: 2,
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}>
							<Typography variant="body2" color="text.secondary">
								Showing {startIndex + 1}-
								{Math.min(endIndex, filteredCircle.length)} of{" "}
								{filteredCircle.length} circles
							</Typography>
						</Box>
					)}

					{/* TO DO: IMPLEMENT CIRCLE SORT COMPONENT: CHECK DESIGN DOCS FOR MORE DETAILS */}

					{!isLoading && circles.length === 0 && (
						<Box
							sx={{
								justifyContent: "center",
								display: "flex",
								alignContent: "center",
								alignItems: "center",
								height: "100%",
								mt: 5,
							}}>
							<Typography variant="h4">
								{error || "Error. Can't get circles, please try again later"}
							</Typography>
						</Box>
					)}

					{filteredCircle.length === 0 && searchInput.trim() !== "" ?
						<Box
							sx={{
								justifyContent: "center",
								display: "flex",
								alignContent: "center",
								alignItems: "center",
								height: "100%",
								mt: 5,
							}}>
							<Typography variant="h6" color="text.secondary">
								No circles found matching your search criteria.
							</Typography>
						</Box>
						: <>
							<Grid container spacing={3}>
								{paginatedCircles.map((circle: any) => {
									// check if user sent join request already
									const isPendingJoinRequest = circle.joinRequests?.some(
										(jrUserId: string) => jrUserId === userData?._id,
									);

									// Either side of the membership counts (circle.members is
									// what the server uses; user.unions can lag behind).
									const isMember =
										userUnionsInclude(userData?.unions as any[], circle._id) ||
										circleMembersInclude(circle.members, userData?._id);

									// Only a cycle that has actually started restricts joining.
									// A scheduled cycle (created, start date still ahead) is open:
									// new members are added to its payout rotation on approval.
									const cycleInProgress =
										circle.hasActiveCycle === true &&
										(circle.activeCycleInfo ?
											isCycleInProgress(circle.activeCycleInfo)
										:	true);
									const cycleStartsLater =
										circle.hasActiveCycle === true &&
										!cycleInProgress &&
										Boolean(circle.activeCycleInfo?.startDate);

									// Check if we're in the end-of-month window
									const inJoinWindow = isEndOfMonthWindow();

									// Disable join button if:
									// - User is already a member
									// - User has a pending join request
									// - A cycle is in progress AND we're NOT in the join window
									const disableJoinButton =
										isMember ||
										isPendingJoinRequest ||
										(cycleInProgress && !inJoinWindow);

									// Get the appropriate message for the join button/overlay
									const joinWindowMessage =
										getJoinWindowMessage(cycleInProgress);

									return (
										<CircleDiscoveryCard
											key={circle._id}
											_id={circle._id}
											circleName={circle.circleName}
											circleImage={circle.circleImage}
											leader={circle.leader}
											configuration={circle.configuration}
											membersCount={circle.membersCount}
											totalMembers={circle.members?.length ?? 0}
											isMember={isMember}
											onJoinButtonClick={() => handleOpenJoinModal(circle._id)}
											onViewButtonClick={() => handleViewCircle(circle._id)}
											onMoreIconClick={handleMoreIconClick}
											joinButtonDisabled={disableJoinButton}
											countryEligibility={checkCircleCountryEligibility(userData, circle)}
											hasActiveCycle={cycleInProgress}
											cycleStartsLater={cycleStartsLater}
											activeCycleInfo={circle.activeCycleInfo}
											inJoinWindow={inJoinWindow}
											joinWindowMessage={joinWindowMessage}
											isPendingJoinRequest={isPendingJoinRequest}
										/>
									);
								})}
							</Grid>

							{!selectedCircle && totalPages > 1 && (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										mt: 4,
										mb: 2,
									}}>
									<Pagination
										count={totalPages}
										page={currentPage}
										onChange={handlePageChange}
										color="primary"
										size="large"
										showFirstButton
										showLastButton
									/>
								</Box>
							)}
						</>
					}

					{selectedCircleId && (
						<JoinRequestForm
							circleId={selectedCircleId}
							open={joinRequestModalOpen}
							onClose={handleCloseJoinModal}
							onActionComplete={handleActionComplete}
						/>
					)}

					<Snackbar
						open={openAlert}
						autoHideDuration={4000}
						onClose={handleCloseAlert}
						anchorOrigin={{ vertical: "top", horizontal: "center" }}>
						<Alert
							onClose={handleCloseAlert}
							variant="filled"
							severity={alertType}
							sx={{ width: "100%" }}>
							{alertMessage}
						</Alert>
					</Snackbar>

					{selectedCircleData && (
						<CircleLimitedDetails
							open={showCircleLimitedDetails}
							handleClose={handleCloseCircleDetails}
							title={selectedCircleData?.circleName}
							circleData={selectedCircleData}
						/>
					)}
				</Main>
			</Box>
		</>
	);
};

export default AllCirclesView;
